import React, { useState } from 'react';
import { FolderType, FileType, FolderWithCount } from '../AccountFiles';
import { Button, Flex, Icon, Text } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { sortItems } from '../Helpers/helpers';
import T from 'Components/Helpers/translate';
import Folder from '../Folder';
import File from '../File';
import './GridView.scss';

type Props = {
	folders: FolderType[];
	folderMap: Map<number, FolderWithCount>;
	files: FileType[];
	openFolder: (folderId: number, name: string) => void;
	deleteFolder: (id: number, totalFiles: number) => void;
	deleteFile: (file: FileType) => void;
	changeFolderFolder: (id: number, folderId: number | null) => void;
	changeFileFolder: (id: number, folderId: number | null) => void;
	changeNameOfFolder: (id: number, name: string) => void;
};

const GridView = ({
	folderMap,
	files,
	folders,
	openFolder,
	deleteFolder,
	deleteFile,
	changeFolderFolder,
	changeFileFolder,
	changeNameOfFolder
}: Props) => {
	const classes = new BemClass('FilesGridView');

	const [sortedFolderField, setSortedFolderField] = useState<keyof FolderType>('name');
	const [sortedFileField, setSortedFileField] = useState<keyof FileType>('name');
	const [foldersAsc, setFoldersAsc] = useState<boolean>(true);
	const [filesAsc, setFilesAsc] = useState<boolean>(true);

	const sortedFolders = sortItems(folders, sortedFolderField, foldersAsc);
	const sortedFiles = sortItems(files, sortedFileField, filesAsc);

	return (
		<div className={classes.b()}>
			{folders.length ? (
				<div className={classes.elem('folders-container').b()}>
					<Flex direction="row" gap="u4">
						<Text align="center" size="xl">
							{T('file.folders')}
						</Text>
						<Button
							size="sm"
							type="link"
							onClick={() => {
								setSortedFolderField('name');
								setFoldersAsc(!foldersAsc);
							}}
							className={classes
								.elem('sort-button')
								.mod({ name: sortedFolderField === 'name' })
								.b()}
						>
							{T('default.name')}
							<Icon
								space="mls"
								name={
									sortedFolderField !== 'name'
										? 'sort'
										: foldersAsc
										? 'sort-alpha-asc'
										: 'sort-alpha-desc'
								}
							/>
						</Button>
					</Flex>
					<div className={classes.elem('folders-container__folders').b()}>
						{sortedFolders.map(folder => (
							<Folder
								folderMap={folderMap}
								deleteFolder={deleteFolder}
								key={folder.id}
								openFolder={openFolder}
								id={folder.id}
								name={folder.name}
								numberOfFolders={folders.length}
								numberOfFiles={files.length}
								changeFolderFolder={changeFolderFolder}
								changeFileFolder={changeFileFolder}
								changeNameOfFolder={changeNameOfFolder}
							/>
						))}
					</div>
				</div>
			) : null}
			<div className={classes.elem('files-container').b()}>
				{files.length ? (
					<Flex direction="row" gap="u4">
						<Text size="xl">{T('file.files')}</Text>
						<Button
							type="link"
							size="sm"
							onClick={() => {
								setSortedFileField('name');
								setFilesAsc(!filesAsc);
							}}
							className={classes
								.elem('sort-button')
								.mod({ name: sortedFileField === 'name' })
								.b()}
						>
							{T('default.name')}
							<Icon
								space="mls"
								name={
									sortedFileField !== 'name'
										? 'sort'
										: filesAsc
										? 'sort-alpha-asc'
										: 'sort-alpha-desc'
								}
							/>
						</Button>
						<Button
							type="link"
							size="sm"
							onClick={() => {
								setSortedFileField('size');
								setFilesAsc(!filesAsc);
							}}
							className={classes
								.elem('sort-button')
								.mod({ size: sortedFileField === 'size' })
								.b()}
						>
							{T('default.size')}
							<Icon
								space="mls"
								name={
									sortedFileField !== 'size'
										? 'sort'
										: filesAsc
										? 'sort-numeric-asc'
										: 'sort-numeric-desc'
								}
							/>
						</Button>
						<Button
							type="link"
							size="sm"
							onClick={() => {
								setSortedFileField('date');
								setFilesAsc(!filesAsc);
							}}
							className={classes
								.elem('sort-button')
								.mod({ date: sortedFileField === 'date' })
								.b()}
						>
							{T('default.date')}
							<Icon
								space="mls"
								name={
									sortedFileField !== 'date'
										? 'sort'
										: filesAsc
										? 'sort-numeric-asc'
										: 'sort-numeric-desc'
								}
							/>
						</Button>
					</Flex>
				) : null}
				<div className={classes.elem('files-container__files').b()}>
					{sortedFiles.map(file => (
						<File
							deleteFile={() => deleteFile(file)}
							key={file.id}
							id={file.id}
							fileType={file.extension}
							name={file.name}
							userRemovable={file.userRemovable}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default GridView;
